import { showModalForm } from '@components/modal-form';
import { useRef, useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { useCurrentUser } from 'client/lib/auth';
import { BtnPrimary } from '@components/buttons';
import { StripeContext, StripeInput } from './checkout/stripe';
import { processStripePayment } from './checkout/stripe-checkout-form';
import { describeCheckoutError, loggableCheckoutError } from './checkout/checkout-logic';
import { reportCheckoutError } from './checkout/report-checkout-error';
import { showError } from '@components/app-error';
import { useTryAsyncData } from 'client/lib/hooks';
import { useBodyScrollLock } from 'client/lib/hooks/use-body-scroll-lock';
import { useEsc } from 'client/utils/use-esc';
import { IcoCheckCircle } from '@components/icons';
import { Spinner } from '@components/spinner';
import { ComponentChildren } from 'preact';
import { AipFoundingRatePrice } from './aip-founding-rate-price';
import { AipTrialClaimSummary } from './aip-trial-claim-summary';
import { detectTimezone } from 'shared/dateutil';
import { Currency } from 'server/types';
import * as pmtfmt from 'shared/payments/fmt';

type UpgradeState = RpxResponse<typeof rpx.aipUpgrade.getAipUpgradeState>;

function UpgradeModalWrapper({
  children,
  onClose,
}: {
  children: ComponentChildren;
  onClose(): void;
}) {
  useBodyScrollLock(true);
  // escapeOnly is load-bearing: the default useEsc behavior ALSO closes on any
  // document click, and clicks on the modal's own submit button bubble there --
  // closing the modal (and unmounting the Stripe elements) mid-payment. The
  // backdrop's onMouseDown handler below covers click-outside dismissal.
  useEsc(onClose, { escapeOnly: true });

  return (
    <section
      class="fixed w-screen h-screen overflow-auto inset-0 z-50 flex items-center justify-center bg-gray-800/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div
        class={[
          'bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-8 sm:p-10',
          'flex flex-col overflow-x-hidden sm:w-(screen-4)',
          'max-h-screen-minus-4 max-w-lg absolute bottom-0 inset-x-0 sm:static',
        ].join(' ')}
      >
        {children}
      </div>
    </section>
  );
}

const benefits = [
  'Participate in course discussions',
  'Join live calls and Q&A sessions',
  'Submit assignments and get feedback',
  'Access the full community',
  'Browse resources and call recordings',
];

function isRetryableError(err: unknown): boolean {
  const status = (err as any)?.statusCode ?? (err as any)?.output?.statusCode;
  if (status && status >= 400 && status < 500) {
    return false;
  }
  return true;
}

async function retryProvision(maxAttempts = 3): Promise<void> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await rpx.aipUpgrade.provisionAipUpgrade();
      return;
    } catch (err) {
      if (attempt === maxAttempts || !isRetryableError(err)) {
        throw err;
      }
      await new Promise((r) => setTimeout(r, 1000 * attempt));
    }
  }
}

function currencyOptionLabel(option: {
  currency: string;
  priceInCents: number;
  interval?: string;
}): string {
  const amount = pmtfmt.price({
    priceInCents: option.priceInCents,
    currency: option.currency as Currency,
    explicitCurrency: true,
  });
  const interval = option.interval ? `/${option.interval}` : '';
  const name = option.currency === 'CAD' ? 'Canadian dollars' : 'US dollars';
  return `${name} — ${amount}${interval}`;
}

function UpgradeModalContent({ resolve }: { resolve(val?: boolean): void }) {
  const user = useCurrentUser()!;
  const [stripeCtx, setStripeCtx] = useState<StripeContext | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const processingRef = useRef(false);
  // The currency the buyer picked, if they picked one. Undefined lets the
  // server choose the default from where they are. Wherever we guess it,
  // they get to say otherwise.
  const [currency, setCurrency] = useState<Currency | undefined>(undefined);

  const upgradeState = useTryAsyncData(
    () =>
      rpx.aipUpgrade.getAipUpgradeState({
        checkAddress: true,
        timezone: detectTimezone(),
        currency,
      }),
    [currency],
  );

  const data = upgradeState.data as UpgradeState | undefined;
  const isLoadError = !upgradeState.isLoading && !data;
  const price = data?.price;
  const freeTrialDays = data?.freeTrialDays ?? 0;
  const hasTrial = freeTrialDays > 0;

  async function handleSubmit() {
    if (!stripeCtx || !price || processingRef.current) {
      return;
    }
    processingRef.current = true;
    setIsProcessing(true);
    setCardError(undefined);
    let succeeded = false;
    try {
      const result = await processStripePayment({
        ctx: stripeCtx,
        user,
        priceId: price.id,
        fallbackAddress: data?.savedAddress,
        processPayment: async (opts) => {
          return rpx.aipUpgrade.purchaseAipUpgrade({
            address: opts.address,
            // Charge the currency this modal quoted. Without it the server
            // would work the default out again from a request that carries no
            // timezone, and could land on the other one.
            currency: price.currency,
          });
        },
      });
      if (!result) {
        setCardError('We could not save your card. Please try again.');
        return;
      }
      if (result.type === 'validationError') {
        // An incomplete Address Element shows no error of its own, so without
        // this the button just resets and the click looks ignored.
        setCardError('Please fill in every field above, then try again.');
        return;
      }
      try {
        await retryProvision();
      } catch {
        showError({
          title: 'Membership activation delayed',
          error:
            'Your payment was successful but we had trouble activating your membership. ' +
            'Please refresh the page — your access should be ready shortly.',
        });
        resolve(true);
        return;
      }
      succeeded = true;
      setIsSuccess(true);
      setTimeout(() => resolve(true), 1500);
    } catch (err) {
      const described = describeCheckoutError(err);
      reportCheckoutError({ step: 'checkout', priceId: price.id, err });
      if (described.kind === 'card') {
        setCardError(described.message);
        console.warn('aip upgrade card error', loggableCheckoutError(err));
      } else {
        showError(err);
      }
    } finally {
      if (!succeeded) {
        processingRef.current = false;
        setIsProcessing(false);
      }
    }
  }

  if (isSuccess) {
    return (
      <UpgradeModalWrapper onClose={() => resolve(true)}>
        <div class="flex flex-col items-center gap-4 py-8">
          <IcoCheckCircle class="size-12 text-green-500" />
          <h2 class="text-2xl font-bold">Welcome to AI Playground!</h2>
          <p class="text-gray-600 text-sm">Your membership is now active.</p>
        </div>
      </UpgradeModalWrapper>
    );
  }

  return (
    <UpgradeModalWrapper onClose={() => resolve(undefined)}>
      <h2 class="text-2xl font-bold mb-2">
        {hasTrial ? 'Claim your free months' : 'Join AI Playground'}
      </h2>
      <p class="text-gray-600 text-sm mb-6">
        {hasTrial
          ? 'Your purchase includes free access to AI Playground. Add a card to start — you can cancel anytime before your first charge.'
          : 'Unlock the full learning experience with an AI Playground membership.'}
      </p>

      <ul class="flex flex-col gap-2 mb-6">
        {benefits.map((b) => (
          <li key={b} class="flex items-center gap-2 text-sm">
            <IcoCheckCircle class="size-5 text-green-500 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {upgradeState.isLoading && (
        <div class="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {isLoadError && (
        <div class="text-center py-8">
          <p class="text-gray-600 text-sm mb-4">
            Unable to load upgrade details. Please try again.
          </p>
          <BtnPrimary onClick={() => location.reload()}>Retry</BtnPrimary>
        </div>
      )}

      {data && price && !upgradeState.isLoading && (
        <>
          {(data.currencyOptions?.length ?? 0) > 1 && (
            <div class="flex gap-2 mb-6">
              {data.currencyOptions!.map((option) => (
                <button
                  key={option.currency}
                  type="button"
                  disabled={isProcessing}
                  onClick={() => {
                    setStripeCtx(undefined);
                    setCardError(undefined);
                    setCurrency(option.currency as Currency);
                  }}
                  class={`flex-1 py-2 px-3 rounded-lg cursor-pointer text-sm font-semibold ${
                    option.currency === price.currency
                      ? 'border-2 border-blue-700 bg-blue-50 text-blue-900'
                      : 'border border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  {currencyOptionLabel(option)}
                </button>
              ))}
            </div>
          )}
          {hasTrial ? (
            <div class="mb-6">
              <AipTrialClaimSummary
                price={price}
                freeTrialDays={freeTrialDays}
                firstChargeAt={data.firstChargeAt}
              />
            </div>
          ) : (
            <div class="border rounded-lg p-4 mb-6 bg-gray-50">
              <div class="flex justify-between items-start gap-3">
                <span class="font-semibold">{data.course.title}</span>
                <span class="text-sm text-right">
                  <AipFoundingRatePrice price={price} />
                </span>
              </div>
            </div>
          )}

          <div class="mb-4">
            <StripeInput
              priceId={price.id}
              customerName={user.name}
              onReady={setStripeCtx}
              collectAddress={data.requiresAddress ?? true}
              disabled={isProcessing}
              amount={price.priceInCents}
              currency={price.currency}
              mode={
                price.paymentType === 'subscription' || price.paymentType === 'paymentplan'
                  ? 'subscription'
                  : 'payment'
              }
            />
            {cardError && (
              <div
                role="alert"
                class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {cardError}
              </div>
            )}
          </div>

          <BtnPrimary
            class="w-full p-3"
            onClick={handleSubmit}
            disabled={isProcessing || !stripeCtx}
          >
            {isProcessing ? 'Processing...' : hasTrial ? 'Start my free months' : 'Upgrade Now'}
          </BtnPrimary>
          <button
            type="button"
            onClick={() => resolve(undefined)}
            disabled={isProcessing}
            class="mt-3 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
          >
            Maybe later
          </button>
        </>
      )}
    </UpgradeModalWrapper>
  );
}

export function showAipUpgradeModal(): Promise<boolean> {
  return showModalForm<boolean>(({ resolve }) => {
    return <UpgradeModalContent resolve={resolve} />;
  }).then((result) => !!result);
}
